import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { Select, message } from "antd";
import Icon from "../../AppIcon";
import Button from "../../ui/Button";
import LoanDocumentViewerModal from "../../../modules/loans/components/shared/LoanDocumentViewerModal";
import { uploadMultipleFiles } from "../../../api/uploads";
import API_BASE_URL from "../../../config/apiBaseUrl";
import { scheduleWindowPrint, escapeHtmlText } from "../../../utils/scheduleWindowPrint";
import { getSuggestedDocsForForm } from "../insuranceDocumentRules";

const sectionHeaderLabel =
  "text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400";

const TAG_COLOR_THEMES = [
  {
    active: "border-emerald-300 bg-emerald-600 text-white",
    soft: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  {
    active: "border-sky-300 bg-sky-600 text-white",
    soft: "border-sky-200 bg-sky-50 text-sky-700",
  },
  {
    active: "border-violet-300 bg-violet-600 text-white",
    soft: "border-violet-200 bg-violet-50 text-violet-700",
  },
  {
    active: "border-amber-300 bg-amber-500 text-white",
    soft: "border-amber-200 bg-amber-50 text-amber-700",
  },
  {
    active: "border-rose-300 bg-rose-600 text-white",
    soft: "border-rose-200 bg-rose-50 text-rose-700",
  },
  {
    active: "border-cyan-300 bg-cyan-600 text-white",
    soft: "border-cyan-200 bg-cyan-50 text-cyan-700",
  },
];

const EW_SUGGESTED_TAGS = [
  "Policy Copy",
  "RC Copy",
  "Invoice",
  "PAN Card",
  "Aadhaar Front",
  "Aadhaar Back",
  "GST Certificate (P1)",
  "GST Certificate (P2)",
  "GST Certificate (P3)",
];

const DOCUMENT_MATRIX = {
  "new-car-insurance": {
    label: "New Car Insurance",
    suggested: [
      "Policy Copy",
      "Invoice",
      "PAN Card",
      "Aadhaar Front",
      "Aadhaar Back",
      "GST Certificate (P1)",
      "GST Certificate (P2)",
      "GST Certificate (P3)",
      "RC Copy",
    ],
  },
  "used-car-insurance": {
    label: "Used Car Insurance",
    suggested: [
      "Policy Copy",
      "RC Copy",
      "PAN Card",
      "Aadhaar Front",
      "Aadhaar Back",
      "GST Certificate (P1)",
      "GST Certificate (P2)",
      "GST Certificate (P3)",
      "Form 29",
      "Form 30 page 1",
      "Form 30 page 2",
      "Inspection Report",
    ],
  },
  "used-car-renewal": {
    label: "Used Car Renewal",
    suggested: [
      "Policy Copy",
      "Previous Year Policy",
      "RC Copy",
      "PAN Card",
      "Aadhaar Front",
      "Aadhaar Back",
      "GST Certificate (P1)",
      "GST Certificate (P2)",
      "GST Certificate (P3)",
    ],
  },
  "policy-already-expired": {
    label: "Policy Already Expired",
    suggested: [
      "Policy Copy",
      "Previous Year Policy",
      "RC Copy",
      "PAN Card",
      "Aadhaar Front",
      "Aadhaar Back",
      "GST Certificate (P1)",
      "GST Certificate (P2)",
      "GST Certificate (P3)",
      "Inspection Report",
    ],
  },
  "ew-policy": {
    label: "EW Policy",
    suggested: [
      "Policy Copy",
      "RC Copy",
      "Invoice",
      "PAN Card",
      "Aadhaar Front",
      "Aadhaar Back",
      "GST Certificate (P1)",
      "GST Certificate (P2)",
      "GST Certificate (P3)",
    ],
  },
};

const initialUiState = {
  scenario: "used-car-renewal",
  searchText: "",
  sortBy: "newest",
  selectedDocId: null,
  isViewerOpen: false,
  isDragging: false,
};

function uiReducer(state, action) {
  switch (action.type) {
    case "SET_SCENARIO":
      return { ...state, scenario: action.payload };
    case "SET_SEARCH":
      return { ...state, searchText: action.payload };
    case "SET_SORT":
      return { ...state, sortBy: action.payload };
    case "SELECT_DOC":
      return { ...state, selectedDocId: action.payload };
    case "OPEN_VIEWER":
      return { ...state, isViewerOpen: true, selectedDocId: action.payload };
    case "CLOSE_VIEWER":
      return { ...state, isViewerOpen: false };
    case "SET_DRAGGING":
      return { ...state, isDragging: action.payload };
    default:
      return state;
  }
}

const getScenarioFromForm = (formData = {}) => {
  const policyCategory = String(formData?.policyCategory || "")
    .trim()
    .toLowerCase();
  if (
    policyCategory.includes("extended warranty") ||
    policyCategory.includes("ew")
  ) {
    return "ew-policy";
  }

  if (formData?.vehicleType === "New Car") return "new-car-insurance";

  const expiryDates = [
    formData?.previousOdExpiryDate,
    formData?.previousTpExpiryDate,
  ]
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  const isExpired =
    expiryDates.length > 0 &&
    Math.max(...expiryDates.map((date) => date.getTime())) < Date.now();

  if (isExpired) return "policy-already-expired";

  const hasPreviousPolicy =
    Boolean(String(formData?.previousPolicyNumber || "").trim()) ||
    Boolean(String(formData?.previousInsuranceCompany || "").trim());

  if (hasPreviousPolicy) return "used-car-renewal";
  return "used-car-insurance";
};

const getPolicyYearLabel = (formData = {}) => {
  const start = String(
    formData?.newPolicyStartDate || formData?.ewCommencementDate || "",
  )
    .trim()
    .slice(0, 4);
  const end = String(
    formData?.newOdExpiryDate ||
      formData?.newTpExpiryDate ||
      formData?.ewExpiryDate ||
      "",
  )
    .trim()
    .slice(0, 4);

  if (start && end) return `${start}-${end}`;
  if (start || end) return start || end;

  const regDate = String(
    formData?.dateOfReg ||
      formData?.registrationDate ||
      formData?.date_of_reg ||
      formData?.dateOfRegistration ||
      "",
  ).trim();
  if (regDate) {
    const parsed = new Date(regDate);
    if (!Number.isNaN(parsed.getTime())) {
      const year = String(parsed.getFullYear()).trim();
      if (year && year !== "1970") return year;
    }
  }

  return "";
};

const getVehiclePolicyLabel = (formData = {}) => {
  const vehicleNumber = String(
    formData?.registrationNumber ||
      formData?.regNo ||
      formData?.registration_no ||
      formData?.rc_redg_no ||
      formData?.registrationNumberNormalized ||
      formData?.vehicleNumber ||
      "",
  ).trim();

  if (vehicleNumber) {
    return vehicleNumber.replace(/[^a-z0-9]/gi, "").toUpperCase();
  }

  return (
    [formData?.vehicleMake, formData?.vehicleModel, formData?.vehicleVariant]
      .filter(Boolean)
      .map((value) =>
        String(value || "")
          .trim()
          .replace(/[^a-z0-9]+/gi, "_")
          .replace(/^_+|_+$/g, ""),
      )
      .filter(Boolean)
      .join("_")
      .trim() || "Vehicle"
  );
};

const getPolicyTagLabel = (formData = {}) => {
  const customerName = String(
    formData?.customerName ||
      formData?.contactPersonName ||
      formData?.companyName ||
      "Customer",
  )
    .trim()
    .replace(/\s+/g, " ")
    .replace(/_+/g, " ");

  return [
    customerName,
    getVehiclePolicyLabel(formData),
    getPolicyYearLabel(formData),
  ]
    .filter(Boolean)
    .join("_");
};

const inferDocumentTag = ({ docName = "", formData = {}, scenario = "" }) => {
  const value = String(docName || "")
    .trim()
    .toLowerCase();
  if (!value) return "";

  if (value.includes("aadhaar") || value.includes("aadhar")) {
    if (value.includes("back")) return "Aadhaar Back";
    return "Aadhaar Front";
  }
  if (value.includes("pan")) return "PAN Card";
  if (value.includes("gst")) {
    if (value.includes("2")) return "GST Certificate (P2)";
    if (value.includes("3")) return "GST Certificate (P3)";
    return "GST Certificate (P1)";
  }
  if (value.includes("invoice")) return "Invoice";
  if (value.includes("form 29") || value.includes("form29")) return "Form 29";
  if (value.includes("form 30") || value.includes("form30")) {
    return value.includes("2") ? "Form 30 page 2" : "Form 30 page 1";
  }
  if (value.includes("inspection")) return "Inspection Report";
  if (value.includes("rc")) return "RC Copy";
  if (value.includes("policy")) {
    if (
      scenario === "used-car-renewal" ||
      scenario === "policy-already-expired"
    ) {
      return value.includes("prev") || value.includes("old")
        ? "Previous Year Policy"
        : "Policy Copy";
    }
    return "Policy Copy";
  }
  if (value.includes("engine")) return "Engine Number";
  if (value.includes("chassis")) return "Chassis Number";

  const reg = String(formData?.registrationNumber || "")
    .trim()
    .toLowerCase();
  if (reg && value.includes(reg.replace(/[^a-z0-9]/g, ""))) return "RC Copy";
  return "";
};

const API_BASE = String(API_BASE_URL || "").replace(/\/+$/, "");

const looksLikeR2Host = (value = "") => {
  try {
    const parsed = new URL(String(value || ""));
    const host = String(parsed.hostname || "").toLowerCase();
    return host.includes("r2.dev") || host.includes("cloudflarestorage.com");
  } catch {
    return false;
  }
};

const buildAccessibleDocumentUrl = (value = "") => {
  const url = String(value || "").trim();
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;

  const isR2Path =
    looksLikeR2Host(url) ||
    url.startsWith("/uploads/") ||
    url.startsWith("uploads/");

  if (!isR2Path || !API_BASE) return url;
  return `${API_BASE}/api/upload/file?url=${encodeURIComponent(url)}`;
};

const getStableDocId = (doc, fallback = "") =>
  String(
    doc?.id ||
      doc?.public_id ||
      doc?.publicId ||
      doc?.storageKey ||
      doc?.url ||
      fallback,
  );

const uniqueList = (values = []) => Array.from(new Set(values.filter(Boolean)));

const formatFileSize = (bytes) => {
  const numeric = Number(bytes || 0);
  if (!numeric) return "0 KB";
  const units = ["Bytes", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(numeric) / Math.log(1024)),
    units.length - 1,
  );
  const value = numeric / 1024 ** unitIndex;
  return `${Math.round(value * 100) / 100} ${units[unitIndex]}`;
};

const extensionFromName = (value = "") => {
  const clean = String(value || "")
    .split("?")[0]
    .split("#")[0];
  const last = clean.split(".").pop();
  return last && last !== clean ? last.toLowerCase() : "";
};

const decodeLoose = (value = "") => {
  const input = String(value || "");
  if (!input) return "";
  try {
    return decodeURIComponent(input);
  } catch {
    return input;
  }
};

const buildDetectionText = (doc = {}) =>
  [
    doc?.type,
    doc?.mimeType,
    doc?.contentType,
    doc?.resource_type,
    doc?.format,
    doc?.name,
    doc?.originalName,
    doc?.original_name,
    doc?.storageKey,
    doc?.storage_key,
    doc?.public_id,
    doc?.publicId,
    doc?.url,
    doc?.previewUrl,
    doc?.rawUrl,
    decodeLoose(doc?.url),
    decodeLoose(doc?.previewUrl),
    decodeLoose(doc?.rawUrl),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const isImageLike = (doc = {}) => {
  const type = String(
    doc?.type || doc?.mimeType || doc?.contentType || "",
  ).toLowerCase();
  const format = String(
    doc?.format ||
      extensionFromName(
        doc?.name ||
          doc?.originalName ||
          doc?.storageKey ||
          doc?.storage_key ||
          doc?.url ||
          doc?.rawUrl,
      ),
  ).toLowerCase();
  const url = buildDetectionText(doc);
  return (
    type.startsWith("image/") ||
    type.includes("image") ||
    [
      "jpg",
      "jpeg",
      "png",
      "webp",
      "gif",
      "bmp",
      "svg",
      "heic",
      "heif",
    ].includes(format) ||
    /\.(jpg|jpeg|png|webp|gif|bmp|svg|heic|heif)(\?|#|$)/i.test(url)
  );
};

const isPdfLike = (doc = {}) => {
  const type = String(
    doc?.type || doc?.mimeType || doc?.contentType || "",
  ).toLowerCase();
  const format = String(
    doc?.format ||
      extensionFromName(
        doc?.name ||
          doc?.originalName ||
          doc?.storageKey ||
          doc?.storage_key ||
          doc?.url ||
          doc?.rawUrl,
      ),
  ).toLowerCase();
  const url = buildDetectionText(doc);
  return (
    type === "application/pdf" ||
    type.includes("pdf") ||
    format === "pdf" ||
    format.includes("pdf") ||
    /\.pdf(\?|#|$)/i.test(url)
  );
};

const safeDateValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** For linked customer/loan docs with explicit sort timestamps */
const docMillis = (doc) => {
  if (!doc) return 0;
  const lt = Number(doc.linkedSortTime);
  if (Number.isFinite(lt) && lt > 0) return lt;
  return safeDateValue(doc.uploadedAt);
};

const LINKED_CATEGORY_SORT_RANK = {
  "customer-kyc": 10,
  "customer-profile": 15,
  "loan-prefile": 25,
  "loan-kyc": 30,
  "loan-vehicle": 40,
  "loan-insurance": 45,
  "loan-postfile": 50,
  "loan-ledger": 55,
  "loan-delivery": 60,
};

const getDocDisplayLabel = (doc, index = -1) => {
  const policyLabel = String(doc?.policyTagLabel || "").trim();
  if (policyLabel) return policyLabel;
  const storedName = String(
    doc?.name || doc?.originalName || doc?.original_name || "",
  ).trim();
  if (storedName) return storedName;
  return index >= 0 ? `Document ${index + 1}` : "Document";
};

const getDocStatus = (doc) => {
  const tag = String(doc?.tag || "").trim();
  if (!tag) {
    return {
      label: "Untagged",
      className: "border-sky-200 bg-sky-50 text-sky-700",
    };
  }
  return {
    label: tag,
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
};

const getTagTheme = (tag) => {
  const key = String(tag || "").trim();
  if (!key) return TAG_COLOR_THEMES[0];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return TAG_COLOR_THEMES[Math.abs(hash) % TAG_COLOR_THEMES.length];
};

const StatTile = ({ label, value, tone = "slate", helper }) => {
  const themes = {
    sky: "bg-blue-50/50 border-blue-100 text-blue-700",
    emerald: "bg-emerald-50/50 border-emerald-100 text-emerald-700",
    slate: "bg-slate-50/50 border-slate-100 text-slate-700",
  };
  const theme = themes[tone] || themes.slate;

  return (
    <div
      className={`rounded-xl border px-5 py-4 shadow-sm transition-all hover:shadow-md ${theme}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] opacity-60">
          {label}
        </div>
        <div className="h-1.5 w-1.5 rounded-full bg-current opacity-30" />
      </div>
      <div className="mt-2 text-2xl font-black tracking-tight leading-none">
        {value}
      </div>
      {helper ? (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-wider opacity-60">
          {helper}
        </div>
      ) : null}
    </div>
  );
};

const SuggestedDocumentsHint = ({ scenarioLabel, suggestedDocs }) => {
  if (!suggestedDocs.length) return null;

  return (
    <div className="mt-3 rounded-xl border border-sky-100/90 bg-gradient-to-br from-sky-50/70 to-white px-4 py-3 shadow-sm">
      <p className="m-0 text-[10px] font-black uppercase tracking-[0.18em] text-sky-800">
        Often needed for {scenarioLabel}
      </p>
      <p className="m-0 mt-1 text-[11px] leading-relaxed text-slate-600">
        Suggestions only — upload and tag what you have. Not required to submit
        the case.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {suggestedDocs.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-sky-200/90 bg-white px-2.5 py-1 text-[11px] font-semibold text-sky-900"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

const QuickTag = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-quick-tag="true"
    className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
      active
        ? getTagTheme(label).active
        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
    }`}
  >
    {label}
  </button>
);

const UploadDropzone = ({
  isDragging,
  uploading,
  onBrowse,
  onDragStateChange,
  onDropFiles,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => {
      if (!uploading) onBrowse();
    }}
    onKeyDown={(event) => {
      if ((event.key === "Enter" || event.key === " ") && !uploading) {
        event.preventDefault();
        onBrowse();
      }
    }}
    onDragOver={(event) => {
      event.preventDefault();
      onDragStateChange(true);
    }}
    onDragEnter={(event) => {
      event.preventDefault();
      onDragStateChange(true);
    }}
    onDragLeave={(event) => {
      if (event.currentTarget.contains(event.relatedTarget)) return;
      onDragStateChange(false);
    }}
    onDrop={(event) => {
      event.preventDefault();
      onDragStateChange(false);
      if (uploading) return;
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length) onDropFiles(files);
    }}
    className={`rounded-xl border-2 border-dashed px-6 py-8 transition-all ${
      isDragging
        ? "border-indigo-400 bg-indigo-50/50 shadow-lg"
        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
    }`}
  >
    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600 shadow-sm">
          <Icon name="CloudUpload" size={26} />
        </div>
        <div className="min-w-0">
          <div className="text-lg font-black text-slate-900 tracking-tight">
            Upload insurance documents
          </div>
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">
            Drag and drop or click to browse. Files are securely stored in
            Cloudflare R2.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg">
          JPG, PNG, PDF, DOC, XLSX
        </span>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (!uploading) onBrowse();
          }}
          className="inline-flex items-center gap-2 h-12 rounded-xl bg-indigo-600 px-6 text-sm font-bold text-white hover:bg-indigo-700 transition-colors shadow-md disabled:opacity-50"
          disabled={uploading}
        >
          {uploading ? (
            <Icon name="Loader2" size={18} className="animate-spin" />
          ) : (
            <Icon name="Upload" size={18} />
          )}
          {uploading ? "Uploading..." : "Browse Files"}
        </button>
      </div>
    </div>
  </div>
);

const PreviewPane = ({
  doc,
  index,
  total,
  onOpenViewer,
  onDownload,
  onPrint,
}) => {
  const imageLike = isImageLike(doc || {});
  const pdfLike = isPdfLike(doc || {});
  const status = getDocStatus(doc || {});
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");

  const rawPreviewSrc = useMemo(
    () =>
      String(doc?.rawUrl || doc?.url || doc?.previewUrl || "").split("#")[0],
    [doc],
  );
  const pdfSourceCandidates = useMemo(
    () =>
      uniqueList([
        rawPreviewSrc,
        doc?.rawUrl,
        doc?.url,
        doc?.previewUrl,
        buildAccessibleDocumentUrl(rawPreviewSrc),
        buildAccessibleDocumentUrl(doc?.rawUrl),
        buildAccessibleDocumentUrl(doc?.url),
        buildAccessibleDocumentUrl(doc?.previewUrl),
      ]).filter(Boolean),
    [doc, rawPreviewSrc],
  );

  const pdfPreviewSrc = useMemo(() => {
    return String(pdfBlobUrl || pdfSourceCandidates[0] || rawPreviewSrc || "");
  }, [pdfBlobUrl, pdfSourceCandidates, rawPreviewSrc]);

  // Same raw + proxied URL variants as the PDF preview above, reused for
  // images: whichever source the doc originally came from (freshly
  // uploaded, linked from a customer/loan profile, etc.), if one URL
  // variant fails to load, try the next instead of just showing a broken
  // image with no explanation.
  const imageSourceCandidates = pdfSourceCandidates;
  const [imageCandidateIndex, setImageCandidateIndex] = useState(0);
  useEffect(() => {
    setImageCandidateIndex(0);
  }, [doc?.id, doc?.url, doc?.previewUrl, doc?.rawUrl]);
  const imagePreviewSrc = imageSourceCandidates[imageCandidateIndex] || "";
  const imagePreviewFailed =
    imageLike &&
    imageSourceCandidates.length > 0 &&
    imageCandidateIndex >= imageSourceCandidates.length;

  useEffect(() => {
    let isCanceled = false;
    let objectUrl = "";
    if (!pdfLike || !pdfSourceCandidates.length) {
      setPdfBlobUrl("");
      return undefined;
    }
    const loadPdf = async () => {
      for (let i = 0; i < pdfSourceCandidates.length; i += 1) {
        try {
          const response = await fetch(pdfSourceCandidates[i], {
            credentials: "include",
          });
          if (!response.ok) throw new Error("PDF fetch failed");
          const blob = await response.blob();
          if (!blob || !blob.size) throw new Error("Empty PDF blob");
          objectUrl = URL.createObjectURL(
            blob.type === "application/pdf"
              ? blob
              : new Blob([blob], { type: "application/pdf" }),
          );
          if (!isCanceled) setPdfBlobUrl(objectUrl);
          return;
        } catch {
          // try next candidate
        }
      }
      if (!isCanceled) setPdfBlobUrl(pdfSourceCandidates[0] || "");
    };
    loadPdf();
    return () => {
      isCanceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfLike, pdfSourceCandidates]);

  if (!doc) {
    return (
      <div className="rounded-xl border border-slate-200/75 bg-white p-6 shadow-sm lg:sticky lg:top-4">
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-6 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
            <Icon name="PanelRightOpen" size={22} />
          </div>
          <div className="text-base font-black text-slate-800">
            Select a document
          </div>
          <p className="mt-2 max-w-xs text-sm text-slate-500">
            Click any document card from the library to open a live preview.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-4">
      <div className="flex items-center justify-between gap-3 px-1 pb-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            Preview
          </div>
          <div className="mt-1 text-sm font-black text-slate-800">
            {Math.min(index + 1, total)}/{total}
          </div>
        </div>
        <span
          className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${status.className}`}
        >
          {status.label}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
        {imageLike ? (
          imagePreviewFailed ? (
            <div className="flex h-[400px] flex-col items-center justify-center gap-3 bg-white px-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Icon name="ImageOff" size={28} />
              </div>
              <div className="text-sm font-bold text-slate-800">
                Preview unavailable
              </div>
              <a
                href={rawPreviewSrc}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-indigo-600 font-bold underline"
              >
                Open manually
              </a>
            </div>
          ) : (
            <img
              src={imagePreviewSrc}
              alt={getDocDisplayLabel(doc, index)}
              loading="lazy"
              className="h-[400px] w-full object-contain bg-white"
              onError={() => setImageCandidateIndex((i) => i + 1)}
            />
          )
        ) : pdfLike ? (
          pdfPreviewSrc ? (
            <object
              data={pdfPreviewSrc}
              type="application/pdf"
              className="h-[400px] w-full bg-white"
            >
              <div className="flex h-[400px] flex-col items-center justify-center gap-3 bg-white px-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <Icon name="FileText" size={28} />
                </div>
                <div className="text-sm font-bold text-slate-800">
                  Preview Blocked
                </div>
                <a
                  href={pdfPreviewSrc}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-indigo-600 font-bold underline"
                >
                  Open manually
                </a>
              </div>
            </object>
          ) : (
            <div className="flex h-[400px] flex-col items-center justify-center gap-3 bg-white px-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Icon name="FileText" size={28} />
              </div>
              <div className="text-sm font-bold text-slate-800">
                Unable to load PDF preview
              </div>
            </div>
          )
        ) : (
          <div className="flex h-[400px] flex-col items-center justify-center gap-3 bg-white px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
              <Icon name="File" size={28} />
            </div>
            <div className="text-sm font-bold text-slate-800">
              Preview unavailable
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-4 px-1">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            Document
          </div>
          <div className="mt-1 break-words text-sm font-bold text-slate-800 leading-tight">
            {getDocDisplayLabel(doc, index)}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            {doc.sizeLabel}
          </span>
          <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            {doc.format || "file"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenViewer(doc)}
            className="flex items-center justify-center gap-2 h-10 rounded-xl border border-slate-200 bg-white text-[11px] font-bold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Icon name="Expand" size={14} />
            Full Viewer
          </button>
          <button
            type="button"
            onClick={() => onPrint(doc, index)}
            className="flex items-center justify-center gap-2 h-10 rounded-xl border border-indigo-200 bg-indigo-50 text-[11px] font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            <Icon name="Printer" size={14} />
            Print
          </button>
          <button
            type="button"
            onClick={() => onDownload(doc, index)}
            className="col-span-2 flex items-center justify-center gap-2 h-10 rounded-xl bg-slate-900 text-[11px] font-bold text-white hover:bg-slate-800 transition-colors"
          >
            <Icon name="Download" size={14} />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

const Step6Documents = ({
  formData,
  documents = [],
  setDocuments,
  schedulePersist,
  docsTaggedCount,
}) => {
  const fileInputRef = useRef(null);
  const [ui, dispatch] = useReducer(uiReducer, initialUiState);
  const [uploading, setUploading] = useState(false);
  const [manualTagInput, setManualTagInput] = useState("");
  const [manualTags, setManualTags] = useState([]);
  const [visibleCount, setVisibleCount] = useState(24);

  useEffect(() => {
    dispatch({ type: "SET_SCENARIO", payload: getScenarioFromForm(formData) });
  }, [formData]);

  const persistSoon = useCallback(() => {
    if (typeof schedulePersist === "function") schedulePersist(250);
  }, [schedulePersist]);

  const activeRequirement =
    DOCUMENT_MATRIX[ui.scenario] || DOCUMENT_MATRIX["used-car-renewal"];
  const policyTagLabel = useMemo(() => getPolicyTagLabel(formData), [formData]);
  const renewalPolicyTagLabel =
    formData?.isRenewal || ui.scenario === "used-car-renewal"
      ? policyTagLabel
      : "";
  const policyYearLabel = useMemo(() => getPolicyYearLabel(formData), [formData]);
  const documentsCustomerName = useMemo(
    () =>
      String(
        formData?.customerName ||
          formData?.contactPersonName ||
          formData?.companyName ||
          "",
      ).trim(),
    [formData],
  );
  const suggestedDocs = useMemo(
    () => getSuggestedDocsForForm(formData),
    [formData],
  );

  const normalizedDocuments = useMemo(
    () =>
      (Array.isArray(documents) ? documents : []).map((doc, index) => {
        const rawUrl = String(
          doc?.url || doc?.previewUrl || doc?.secure_url || "",
        ).trim();
        const previewUrl = buildAccessibleDocumentUrl(rawUrl);

        const originalName = String(
          doc?.originalName ||
            doc?.original_name ||
            doc?.name ||
            `Document ${index + 1}`,
        ).trim();

        const fallbackFormat =
          extensionFromName(originalName || rawUrl) ||
          String(doc?.format || "")
            .toLowerCase()
            .split("/")
            .pop();

        const mimeType = String(
          doc?.type || doc?.resource_type || doc?.format || "",
        );
        const sizeBytes =
          typeof doc?.size === "number"
            ? doc.size
            : Number(doc?.sizeBytes || 0) || 0;
        const sizeUnknown = Boolean(doc?.sizeUnknown);

        return {
          ...doc,
          id: getStableDocId(doc, `doc-${index}`),
          name: originalName,
          originalName,
          rawUrl,
          url: previewUrl || rawUrl,
          previewUrl: previewUrl || rawUrl,
          sizeBytes,
          sizeLabel:
            doc?.sizeLabel || (sizeUnknown ? "Size unknown" : formatFileSize(sizeBytes)),
          uploadedAt: doc?.uploadedAt || "",
          uploadedBy: doc?.uploadedBy || "",
          type: mimeType,
          format: String(doc?.format || fallbackFormat || "file").toLowerCase(),
          tag: String(doc?.tag || "").trim(),
          policyTagLabel: String(
            doc?.policyTagLabel || renewalPolicyTagLabel || "",
          ).trim(),
        };
      }),
    [documents, renewalPolicyTagLabel],
  );

  const baseSuggestedTags = useMemo(() => {
    if (ui.scenario === "ew-policy") return EW_SUGGESTED_TAGS;
    return activeRequirement.suggested || [];
  }, [activeRequirement.suggested, ui.scenario]);

  const persistedCustomTags = useMemo(() => {
    const baseSet = new Set(
      baseSuggestedTags.map((x) => String(x || "").trim()),
    );
    return uniqueList(
      normalizedDocuments
        .map((doc) => String(doc?.tag || "").trim())
        .filter((tag) => tag && !baseSet.has(tag)),
    );
  }, [baseSuggestedTags, normalizedDocuments]);

  const suggestedTags = useMemo(
    () =>
      uniqueList([...baseSuggestedTags, ...persistedCustomTags, ...manualTags]),
    [baseSuggestedTags, manualTags, persistedCustomTags],
  );

  useEffect(() => {
    const sourceDocs = Array.isArray(documents) ? documents : [];
    const nextDocs = sourceDocs.map((doc, index) => {
      const existingTag = String(doc?.tag || "").trim();
      if (existingTag) return doc;
      const inferredTag = inferDocumentTag({
        docName:
          doc?.originalName ||
          doc?.original_name ||
          doc?.name ||
          doc?.url ||
          `document-${index + 1}`,
        formData,
        scenario: ui.scenario,
      });
      return inferredTag ? { ...doc, tag: inferredTag } : doc;
    });

    const changed = nextDocs.some(
      (doc, index) =>
        String(doc?.tag || "").trim() !==
        String(sourceDocs[index]?.tag || "").trim(),
    );
    if (!changed) return;

    setDocuments(nextDocs);
    persistSoon();
  }, [documents, formData, persistSoon, setDocuments, ui.scenario]);

  const safeTaggedCount =
    typeof docsTaggedCount === "number"
      ? docsTaggedCount
      : normalizedDocuments.filter((doc) => String(doc?.tag || "").trim())
          .length;

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = ui.searchText.trim().toLowerCase();

    const filtered = normalizedDocuments.filter((doc, index) => {
      if (!normalizedSearch) return true;
      const haystack = [
        getDocDisplayLabel(doc, index),
        doc.tag,
        doc.format,
        doc.uploadedBy,
        doc.uploadedAt,
        doc.linkedOrigin,
        doc.linkedOriginCategory,
        doc.linkedLoanVehicleLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });

    return [...filtered].sort((a, b) => {
      if (ui.sortBy === "tagged") {
        const aTagged = Boolean(String(a.tag || "").trim());
        const bTagged = Boolean(String(b.tag || "").trim());
        if (aTagged !== bTagged) return aTagged ? -1 : 1;
      }
      if (ui.sortBy === "untagged") {
        const aTagged = Boolean(String(a.tag || "").trim());
        const bTagged = Boolean(String(b.tag || "").trim());
        if (aTagged !== bTagged) return aTagged ? 1 : -1;
      }
      if (ui.sortBy === "name") {
        return String(getDocDisplayLabel(a, 0)).localeCompare(
          String(getDocDisplayLabel(b, 0)),
        );
      }
      if (ui.sortBy === "timeline") {
        const ra =
          LINKED_CATEGORY_SORT_RANK[String(a.linkedOriginCategory || "")] ??
          1000;
        const rb =
          LINKED_CATEGORY_SORT_RANK[String(b.linkedOriginCategory || "")] ??
          1000;
        if (ra !== rb) return ra - rb;
        return docMillis(a) - docMillis(b);
      }
      if (ui.sortBy === "oldest") {
        return docMillis(a) - docMillis(b);
      }
      return docMillis(b) - docMillis(a);
    });
  }, [normalizedDocuments, ui.searchText, ui.sortBy]);

  const selectedDoc = useMemo(() => {
    if (!filteredDocuments.length) return null;
    const selected = filteredDocuments.find(
      (doc) => getStableDocId(doc) === ui.selectedDocId,
    );
    return selected || filteredDocuments[0];
  }, [filteredDocuments, ui.selectedDocId]);

  const visibleDocuments = useMemo(
    () => filteredDocuments.slice(0, visibleCount),
    [filteredDocuments, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(24);
  }, [ui.searchText, ui.sortBy, ui.scenario]);

  useEffect(() => {
    if (!filteredDocuments.length) {
      dispatch({ type: "SELECT_DOC", payload: null });
      return;
    }
    if (!ui.selectedDocId) {
      dispatch({
        type: "SELECT_DOC",
        payload: getStableDocId(filteredDocuments[0]),
      });
      return;
    }
    const exists = filteredDocuments.some(
      (doc) => getStableDocId(doc) === ui.selectedDocId,
    );
    if (!exists) {
      dispatch({
        type: "SELECT_DOC",
        payload: getStableDocId(filteredDocuments[0]),
      });
    }
  }, [filteredDocuments, ui.selectedDocId]);

  const viewerDocuments = useMemo(
    () =>
      filteredDocuments.map((doc, index) => ({
        ...doc,
        original_name: doc.original_name || doc.originalName || doc.name || "",
        storageKey: doc.storageKey || doc.storage_key || "",
        public_id: doc.public_id || doc.publicId || "",
        resource_type: doc.resource_type || "",
        mimeType: doc.mimeType || doc.type || "",
        id: getStableDocId(doc, `viewer-${index}`),
        name: getDocDisplayLabel(doc, index),
        originalName: doc.originalName || doc.name || "",
        tag: doc.tag || "",
        documentStage: doc.documentStage || "",
        rawUrl: doc.rawUrl || doc.url || doc.previewUrl || "",
        url:
          doc.rawUrl ||
          doc.url ||
          doc.previewUrl ||
          buildAccessibleDocumentUrl(
            doc.rawUrl || doc.url || doc.previewUrl || "",
          ),
        previewUrl:
          doc.rawUrl ||
          doc.url ||
          doc.previewUrl ||
          buildAccessibleDocumentUrl(
            doc.rawUrl || doc.url || doc.previewUrl || "",
          ),
        proxyUrl: buildAccessibleDocumentUrl(
          doc.rawUrl || doc.url || doc.previewUrl || "",
        ),
        isPdf:
          Boolean(doc.isPdf) ||
          isPdfLike({
            ...doc,
            name: doc.originalName || doc.name,
            format:
              doc.format ||
              extensionFromName(
                doc.originalName ||
                  doc.original_name ||
                  doc.storageKey ||
                  doc.name ||
                  doc.url,
              ),
          }),
        isImage: Boolean(doc.isImage) || isImageLike(doc),
        type:
          doc.type ||
          (isImageLike(doc)
            ? `image/${String(doc.format || "jpeg").toLowerCase()}`
            : isPdfLike(doc)
              ? "application/pdf"
              : ""),
        format:
          doc.format ||
          extensionFromName(
            doc.originalName ||
              doc.original_name ||
              doc.storageKey ||
              doc.name ||
              doc.url,
          ) ||
          (isPdfLike(doc) ? "pdf" : isImageLike(doc) ? "jpg" : "file"),
      })),
    [filteredDocuments],
  );

  const viewerIndex = useMemo(() => {
    if (!selectedDoc) return 0;
    const idx = viewerDocuments.findIndex(
      (doc) => getStableDocId(doc) === getStableDocId(selectedDoc),
    );
    return idx >= 0 ? idx : 0;
  }, [selectedDoc, viewerDocuments]);

  const openFileExplorer = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const appendFiles = useCallback(
    async (files) => {
      const pickedFiles = Array.from(files || []).filter(Boolean);
      if (!pickedFiles.length) return;

      const MAX_FILES = 10;
      const MAX_SIZE = 20 * 1024 * 1024;
      const limitedFiles = pickedFiles.slice(0, MAX_FILES);
      const validFiles = limitedFiles.filter(
        (file) => Number(file?.size || 0) <= MAX_SIZE,
      );

      if (pickedFiles.length > MAX_FILES) {
        message.warning(
          `Only 10 files can be uploaded at once. ${pickedFiles.length - MAX_FILES} skipped.`,
        );
      }

      if (validFiles.length < limitedFiles.length) {
        message.warning(
          `${limitedFiles.length - validFiles.length} file(s) skipped (exceeds 20MB limit)`,
        );
      }

      if (!validFiles.length) return;

      setUploading(true);
      try {
        const uploaded = await uploadMultipleFiles(validFiles);
        const nowIso = new Date().toISOString();
        const incoming = uploaded.map((file, index) => {
          const rawUrl = String(file?.secure_url || file?.url || "").trim();
          const originalName = String(
            file?.original_name || file?.name || `Document ${index + 1}`,
          ).trim();
          const format =
            extensionFromName(originalName || rawUrl) ||
            String(file?.format || "")
              .toLowerCase()
              .split("/")
              .pop() ||
            "file";

          const mimeType = String(file?.format || file?.type || "").trim();
          const sizeBytes = Number(file?.size || 0);
          const storageKey = String(
            file?.public_id || file?.storageKey || "",
          ).trim();
          const inferredTag = inferDocumentTag({
            docName: originalName || rawUrl,
            formData,
            scenario: ui.scenario,
          });

          return {
            id:
              storageKey ||
              `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
            name: renewalPolicyTagLabel || originalName,
            originalName,
            size: sizeBytes,
            sizeBytes,
            sizeLabel: formatFileSize(sizeBytes),
            type: mimeType,
            format,
            tag: inferredTag,
            uploadedAt: nowIso,
            uploadedBy: "Current User",
            storageKey,
            url: rawUrl,
            previewUrl: buildAccessibleDocumentUrl(rawUrl),
            source: "uploaded-r2",
            policyTagLabel: renewalPolicyTagLabel,
          };
        });

        setDocuments((prev) => [
          ...(Array.isArray(prev) ? prev : []),
          ...incoming,
        ]);
        persistSoon();
        message.success(
          `${incoming.length} document${incoming.length > 1 ? "s" : ""} uploaded`,
        );
      } catch (err) {
        console.error("[Insurance][Documents] upload failed:", err);
        const errorMessage = String(err?.message || "Document upload failed");
        if (errorMessage.toLowerCase().includes("limit")) {
          message.error(
            "Upload failed: max 10 files and 20MB per file are allowed.",
          );
        } else {
          message.error(errorMessage);
        }
      } finally {
        setUploading(false);
      }
    },
    [
      formData,
      persistSoon,
      renewalPolicyTagLabel,
      setDocuments,
      ui.scenario,
    ],
  );

  const handlePickedFiles = useCallback(
    (event) => {
      appendFiles(event.target.files || []);
      event.target.value = "";
    },
    [appendFiles],
  );

  const updateDocTag = useCallback(
    (docId, tagValue) => {
      const finalTag = String(tagValue || "").trim();
      setDocuments((prev) =>
        (Array.isArray(prev) ? prev : []).map((doc) => {
          if (getStableDocId(doc) !== docId) return doc;
          const currentTag = String(doc?.tag || "").trim();
          return {
            ...doc,
            tag: currentTag === finalTag ? "" : finalTag,
          };
        }),
      );
      persistSoon();
    },
    [persistSoon, setDocuments],
  );

  const addManualTag = useCallback(() => {
    const nextTag = String(manualTagInput || "").trim();
    if (!nextTag) return;
    setManualTags((prev) => uniqueList([...prev, nextTag]));
    setManualTagInput("");
  }, [manualTagInput]);

  const removeManualTag = useCallback((tagToRemove) => {
    const normalized = String(tagToRemove || "").trim();
    if (!normalized) return;
    setManualTags((prev) =>
      prev.filter((tag) => String(tag || "").trim() !== normalized),
    );
  }, []);

  const removeDocument = useCallback(
    (docId) => {
      setDocuments((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (item) => getStableDocId(item) !== docId,
        ),
      );
      persistSoon();
    },
    [persistSoon, setDocuments],
  );

  const handleDownload = useCallback((doc, index = 0) => {
    const href = doc?.previewUrl || doc?.url || doc?.rawUrl;
    if (!href) return;

    const ext =
      extensionFromName(doc?.format || doc?.name || doc?.url || "") || "file";
    const nameBase = getDocDisplayLabel(doc, index)
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .toLowerCase();

    const link = document.createElement("a");
    link.href = href;
    link.download = `${nameBase || `document-${index + 1}`}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handlePrint = useCallback((doc) => {
    const rawHref = String(
      doc?.previewUrl || doc?.url || doc?.rawUrl || "",
    ).trim();
    if (!rawHref) {
      message.warning("No document available for print.");
      return;
    }
    const href = buildAccessibleDocumentUrl(rawHref);
    // Try the proxied URL first, then fall back to the raw source (same
    // multi-candidate approach the "Full Viewer" PDF preview already uses
    // successfully above) — a single failed fetch shouldn't fall through to
    // just navigating the raw proxy URL instead of printing.
    const fetchCandidates = uniqueList([href, rawHref]);

    const printFromBlob = async () => {
      let objectUrl = "";
      try {
        let response = null;
        for (const candidate of fetchCandidates) {
          try {
            const res = await fetch(candidate);
            if (res.ok) {
              response = res;
              break;
            }
          } catch {
            // try next candidate
          }
        }
        if (!response) throw new Error("Unable to load document for print.");
        const blob = await response.blob();
        if (!blob || !blob.size) throw new Error("Empty document.");

        if (blob.type === "application/pdf") {
          objectUrl = URL.createObjectURL(blob);
          const win = window.open(objectUrl, "_blank");
          if (!win) throw new Error("Popup blocked while opening PDF.");
          scheduleWindowPrint(win, { loadDelayMs: 650, fallbackMs: 2000 });
          return;
        }

        objectUrl = URL.createObjectURL(blob);

        if (blob.type.startsWith("image/")) {
          // Print via a real HTML document instead of pointing an iframe's
          // `src` straight at the image blob: a raw-binary iframe.src loads
          // wrapped in the browser's bare-bones image viewer with no layout
          // control, and (since `src` was set before `onload` was attached)
          // the load event could fire and be missed before print() ever ran.
          const win = window.open("", "_blank", "width=900,height=1000");
          if (!win) throw new Error("Popup blocked while opening image.");
          win.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtmlText(doc?.name || doc?.originalName || "Document")}</title>
    <style>
      html, body { margin: 0; padding: 0; }
      img { display: block; max-width: 100%; height: auto; }
      @media print { img { width: 100%; } }
    </style>
  </head>
  <body>
    <img src="${objectUrl}" alt="" />
  </body>
</html>`);
          win.document.close();
          const img = win.document.querySelector("img");
          if (img) {
            if (img.complete) {
              scheduleWindowPrint(win, { loadDelayMs: 200, fallbackMs: 1500 });
            } else {
              img.addEventListener(
                "load",
                () => scheduleWindowPrint(win, { loadDelayMs: 150, fallbackMs: 1200 }),
                { once: true },
              );
              img.addEventListener(
                "error",
                () => scheduleWindowPrint(win, { loadDelayMs: 0, fallbackMs: 800 }),
                { once: true },
              );
            }
          } else {
            scheduleWindowPrint(win, { loadDelayMs: 200, fallbackMs: 1500 });
          }
          return;
        }

        // Non-PDF, non-image (e.g. Word docs) — browsers can't render these
        // inline, so open it in a new tab and let the OS/browser's own
        // viewer handle printing.
        window.open(objectUrl, "_blank");
      } catch (error) {
        message.warning(error?.message || "Unable to print this document.");
        // Fallback
        window.open(href, "_blank");
      }
      // Don't revoke objectUrl immediately — the opened tab/window still needs it.
    };
    printFromBlob();
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (!normalizedDocuments.length) {
      message.info("No documents available to download.");
      return;
    }
    for (let index = 0; index < normalizedDocuments.length; index += 1) {
      handleDownload(normalizedDocuments[index], index);
      // avoid browser dropping multiple instant downloads
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 140));
    }
    message.success(
      `Download started for ${normalizedDocuments.length} documents.`,
    );
  }, [handleDownload, normalizedDocuments]);

  const handleViewerIndexChange = useCallback(
    (idx) => {
      const nextDocument = viewerDocuments[idx];
      if (!nextDocument) return;
      dispatch({ type: "SELECT_DOC", payload: getStableDocId(nextDocument) });
    },
    [viewerDocuments],
  );

  const selectedDocIndex = useMemo(() => {
    if (!selectedDoc) return 0;
    const idx = filteredDocuments.findIndex(
      (doc) => getStableDocId(doc) === getStableDocId(selectedDoc),
    );
    return idx >= 0 ? idx : 0;
  }, [filteredDocuments, selectedDoc]);

  const getQuickTagsForDoc = useCallback(
    (doc) => {
      const docId = getStableDocId(doc);
      const usedElsewhere = new Set(
        normalizedDocuments
          .filter((item) => getStableDocId(item) !== docId)
          .map((item) => String(item?.tag || "").trim())
          .filter(Boolean),
      );

      return suggestedTags.filter((tag) => {
        const normalized = String(tag || "").trim();
        if (!normalized) return false;
        return (
          !usedElsewhere.has(normalized) ||
          normalized === String(doc?.tag || "").trim()
        );
      });
    },
    [normalizedDocuments, suggestedTags],
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handlePickedFiles}
      />
      <div className="insurance-step6-docs -mt-2 flex flex-col gap-3">
        <section className="overflow-hidden rounded-xl border border-slate-200/65 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 shadow-sm">
          <div className="px-5 py-3.5 md:px-6 md:py-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <div className={sectionHeaderLabel}>Documentation</div>
                <div className="truncate text-[22px] font-black tracking-tight text-slate-800">
                  Document workspace
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  Customer profile and loan files auto-appear when a customer is
                  linked. New uploads go to Cloudflare R2.
                </div>
              </div>

              <div className="w-full xl:w-auto xl:min-w-[280px]">
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                    Policy flow
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-800">
                    {activeRequirement.label}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Upload any documents you have — nothing is mandatory to
                    submit.
                  </div>
                  {policyTagLabel ? (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      {documentsCustomerName ? (
                        <span className="text-[11px] font-semibold leading-relaxed text-slate-700">
                          {documentsCustomerName}
                        </span>
                      ) : null}
                      {policyYearLabel ? (
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-black text-indigo-700">
                          Policy {policyYearLabel}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <StatTile
                label="Uploaded"
                value={normalizedDocuments.length}
                tone="sky"
                helper="Case uploads + customer / loan library"
              />
              <StatTile
                label="Tagged"
                value={safeTaggedCount}
                tone="emerald"
                helper={`${Math.max(normalizedDocuments.length - safeTaggedCount, 0)} left to classify`}
              />
              <StatTile
                label="Suggested"
                value={suggestedTags.length}
                helper="Quick tags available for this policy"
              />
            </div>

            <div className="mt-3 rounded-[20px] border border-slate-200 bg-white/90 p-3 shadow-sm">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                  Suggested document tags
                </div>
                <div className="flex w-full items-center gap-2 md:w-auto md:max-w-[360px]">
                  <input
                    value={manualTagInput}
                    onChange={(event) => setManualTagInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addManualTag();
                      }
                    }}
                    placeholder="Manual tag"
                    className="h-8 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300"
                  />
                  <Button
                    variant="outline"
                    iconName="Plus"
                    size="sm"
                    onClick={addManualTag}
                    className="h-8 rounded-xl border-slate-200 bg-white px-2.5 hover:bg-slate-50"
                  />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {suggestedTags.map((item) => {
                  const normalized = String(item || "").trim();
                  const isManual = manualTags.some(
                    (tag) => String(tag || "").trim() === normalized,
                  );
                  const theme = getTagTheme(item);
                  return (
                    <span
                      key={item}
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold ${theme.soft}`}
                    >
                      {item}
                      {isManual ? (
                        <button
                          type="button"
                          aria-label={`Remove ${item}`}
                          onClick={() => removeManualTag(item)}
                          className="rounded-full p-0.5 text-current/80 transition hover:bg-black/10 hover:text-current"
                        >
                          <Icon name="X" size={10} />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>

            <SuggestedDocumentsHint
              scenarioLabel={activeRequirement.label}
              suggestedDocs={suggestedDocs}
            />
          </div>
        </section>

        <UploadDropzone
          isDragging={ui.isDragging}
          uploading={uploading}
          onBrowse={openFileExplorer}
          onDragStateChange={(value) =>
            dispatch({ type: "SET_DRAGGING", payload: value })
          }
          onDropFiles={appendFiles}
        />

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4 md:px-6">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
                    Library
                  </div>
                  <div className="mt-1 text-[22px] font-black tracking-tight text-slate-800">
                    Documents
                  </div>
                  {policyTagLabel ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {documentsCustomerName ? (
                        <span className="text-sm font-semibold text-slate-600">
                          {documentsCustomerName}
                        </span>
                      ) : null}
                      {policyYearLabel ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-black text-indigo-700">
                          <Icon name="CalendarDays" size={12} />
                          Policy {policyYearLabel}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="flex w-full flex-col gap-2 xl:w-auto xl:min-w-[560px] xl:flex-row xl:items-center">
                  <div className="relative flex-1">
                    <Icon
                      name="Search"
                      size={15}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={ui.searchText}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_SEARCH",
                          payload: e.target.value,
                        })
                      }
                      placeholder="Search by tag, uploader or file type"
                      className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white"
                    />
                  </div>

                  <Select
                    allowClear
                    value={ui.sortBy}
                    style={{ width: "100%", maxWidth: 190 }}
                    options={[
                      { value: "newest", label: "Newest first" },
                      { value: "oldest", label: "Oldest first" },
                      { value: "timeline", label: "Timeline (profile → loan)" },
                      { value: "name", label: "Name A-Z" },
                      { value: "tagged", label: "Tagged first" },
                      { value: "untagged", label: "Untagged first" },
                    ]}
                    onChange={(value) =>
                      dispatch({ type: "SET_SORT", payload: value })
                    }
                  />
                  <Button
                    variant="outline"
                    iconName="Download"
                    iconPosition="left"
                    size="sm"
                    onClick={handleDownloadAll}
                    className="h-11 shrink-0 rounded-xl border-violet-200 bg-violet-50 px-4 text-violet-700 hover:bg-violet-100"
                  >
                    Download all
                  </Button>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 md:px-6">
              {normalizedDocuments.length === 0 ? (
                <div className="rounded-[26px] border border-dashed border-slate-300 bg-slate-50/80 px-6 py-14 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[22px] bg-white text-sky-600 shadow-sm ring-1 ring-slate-200">
                    <Icon name="Files" size={26} />
                  </div>
                  <div className="text-lg font-black tracking-tight text-slate-800">
                    No documents yet
                  </div>
                  <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                    Upload the first batch and use Quick Tag on each row.
                  </p>
                  <div className="mt-5">
                    <Button
                      variant="default"
                      iconName={uploading ? "Loader2" : "Upload"}
                      iconPosition="left"
                      size="sm"
                      onClick={openFileExplorer}
                      className="h-11 rounded-xl bg-sky-600 px-5 text-white hover:bg-sky-700"
                    >
                      {uploading ? "Uploading..." : "Upload documents"}
                    </Button>
                  </div>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
                    <Icon name="SearchX" size={22} />
                  </div>
                  <div className="text-base font-bold text-slate-800">
                    No documents in this filter
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    Try another search or clear the search input.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {visibleDocuments.map((doc, index) => {
                    const docId = getStableDocId(doc, `row-${index}`);
                    const status = getDocStatus(doc);
                    const isSelected =
                      selectedDoc && getStableDocId(selectedDoc) === docId;
                    const imageLike = isImageLike(doc);
                    const pdfLike = isPdfLike(doc);
                    const quickTags = getQuickTagsForDoc(doc);

                    return (
                      <div
                        key={docId}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          dispatch({ type: "SELECT_DOC", payload: docId })
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            dispatch({ type: "SELECT_DOC", payload: docId });
                          }
                        }}
                        className={`rounded-xl border p-4 transition-all duration-200 ${
                          isSelected
                            ? "border-indigo-400 bg-indigo-50/30 ring-1 ring-indigo-400/20 shadow-md"
                            : "border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm"
                        }`}
                      >
                        <div className="flex flex-col gap-4 lg:flex-row">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              dispatch({ type: "SELECT_DOC", payload: docId });
                            }}
                            className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-slate-50 text-left"
                          >
                            {imageLike ? (
                              <>
                                <img
                                  src={doc.previewUrl || doc.url}
                                  alt={getDocDisplayLabel(doc, index)}
                                  className="h-full w-full object-cover"
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                    const fallback = event.currentTarget.nextElementSibling;
                                    if (fallback) fallback.style.display = "flex";
                                  }}
                                />
                                <div
                                  className="h-full w-full items-center justify-center bg-slate-50"
                                  style={{ display: "none" }}
                                >
                                  <Icon name="ImageOff" size={22} className="text-slate-300" />
                                </div>
                              </>
                            ) : pdfLike ? (
                              <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-white">
                                <Icon
                                  name="FileText"
                                  size={30}
                                  className="text-rose-500"
                                />
                                <span className="text-[10px] font-semibold text-slate-500">
                                  PDF
                                </span>
                              </div>
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-slate-50">
                                <Icon
                                  name="File"
                                  size={30}
                                  className="text-slate-400"
                                />
                              </div>
                            )}
                            {pdfLike ? (
                              <span className="absolute bottom-2 left-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                PDF
                              </span>
                            ) : null}
                          </button>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      dispatch({
                                        type: "SELECT_DOC",
                                        payload: docId,
                                      });
                                    }}
                                    className="truncate text-left text-sm font-black text-slate-800 md:text-base"
                                  >
                                    {getDocDisplayLabel(doc, index)}
                                  </button>
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                                    {doc.format || "file"}
                                  </span>
                                  <span
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${status.className}`}
                                  >
                                    {status.label}
                                  </span>
                                  {doc.linkedOrigin ? (
                                    <span className="max-w-[min(100%,420px)] truncate rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-violet-800">
                                      {doc.linkedOrigin}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1">
                                    {doc.sizeLabel}
                                  </span>
                                  {doc.uploadedBy ? (
                                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                                      {doc.uploadedBy}
                                    </span>
                                  ) : null}
                                  {doc.uploadedAt ? (
                                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                                      {new Date(doc.uploadedAt).toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                                  Quick Tag
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {quickTags.map((tag) => (
                                    <QuickTag
                                      key={`${docId}-${tag}`}
                                      label={tag}
                                      active={
                                        String(doc.tag || "").trim() === tag
                                      }
                                      onClick={() => updateDocTag(docId, tag)}
                                    />
                                  ))}
                                </div>
                              </div>

                              <div className="flex w-full flex-col gap-2 lg:w-[230px]">
                                <div className="flex flex-wrap gap-2 sm:justify-end">
                                  <Button
                                    variant="outline"
                                    iconName="Eye"
                                    iconPosition="left"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      dispatch({
                                        type: "OPEN_VIEWER",
                                        payload: docId,
                                      });
                                    }}
                                    className="h-10 rounded-xl border-slate-200 bg-white px-4 hover:bg-slate-50"
                                  >
                                    Open viewer
                                  </Button>
                                  <Button
                                    variant="outline"
                                    iconName="Download"
                                    iconPosition="left"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDownload(doc, index);
                                    }}
                                    className="h-10 rounded-xl border-sky-200 bg-sky-50 px-4 text-sky-700 hover:bg-sky-100"
                                  >
                                    Download
                                  </Button>
                                  <Button
                                    variant="outline"
                                    iconName="Trash2"
                                    iconPosition="left"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      removeDocument(docId);
                                    }}
                                    className="h-10 rounded-xl border-rose-200 bg-rose-50 px-4 text-rose-700 hover:bg-rose-100"
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filteredDocuments.length > visibleDocuments.length ? (
                    <div className="flex justify-center pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ChevronDown"
                        iconPosition="left"
                        onClick={() =>
                          setVisibleCount((prev) =>
                            Math.min(prev + 24, filteredDocuments.length),
                          )
                        }
                        className="h-10 rounded-xl border-blue-200 bg-blue-50 px-4 text-blue-700 hover:bg-blue-100"
                      >
                        Load more (
                        {filteredDocuments.length - visibleDocuments.length}{" "}
                        left)
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <PreviewPane
            doc={selectedDoc}
            index={selectedDocIndex}
            total={filteredDocuments.length}
            onOpenViewer={(doc) =>
              dispatch({ type: "OPEN_VIEWER", payload: getStableDocId(doc) })
            }
            onDownload={handleDownload}
            onPrint={handlePrint}
          />
        </section>
      </div>

      <LoanDocumentViewerModal
        open={Boolean(ui.isViewerOpen && viewerDocuments.length)}
        title="Insurance Document Viewer"
        documents={viewerDocuments}
        currentIndex={viewerIndex}
        onIndexChange={handleViewerIndexChange}
        onClose={() => dispatch({ type: "CLOSE_VIEWER" })}
      />
    </>
  );
};

export default Step6Documents;
